import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaBullseye,
  FaBox,
  FaExclamationTriangle,
  FaSync,
  FaHistory,
  FaChevronDown,
  FaStore,
  FaChartBar,
} from 'react-icons/fa';
import { GiCorn } from 'react-icons/gi';
import feedingService from '../../map/services/feedingService';
import { huntService } from '../../features/services/huntService';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YSelect } from '../../../ui/YSelect';
import { YLogSearchInput } from '../../../ui/YLogSearchInput';
import { YPagination } from '../../../ui/YPagination';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { YSheet } from '../../../ui/sheet/YSheet';
import { useToast } from '../../../components/ui/ToastProvider';
import { buildFeedInventorySeries, filterFeedEventsByType } from '../../../services/feed/feedAnalyticsTransforms';
import { buildConsumptionSummary } from '../../../services/feed/feedConsumptionService';
import { readQueue, enqueueOfflineEvent, flushQueue } from '../../../services/feed/offlineFeedQueue';
import SeedFeedFinder from '../components/SeedFeedFinder';
import { MyStoresCard } from '../components/MyStoresCard';
import { ToolkitShellTabs } from '../components/ToolkitShellTabs';
import { YFeedOutlookChart, useBrushSelection } from '../../../ui/charts';
import { localDateFrom } from '../../../utils/dateUtils';
import api from '../../../utils/api';
import {
  getFeedLevelStatus,
  getFeedTypeDotColor,
  formatFeedTypeLabel,
  resolveFeedEventFeedType,
  countFeedersNeedingAttention,
  buildUrgentRefillTasks,
  isClubFeeder,
  personalFeedStations,
  buildClubNameMap,
  resolveFeederScopeLabel,
  FEED_STAND_SORT_OPTIONS,
  FEED_CONSUMPTION_RATE_OPTIONS,
  sortFeedTargets,
  isFeederActive,
  getConsumptionRateLbsPerDay,
  getConsumptionRateOptionLabel,
  getFeedingModeLabel,
  estimateCapacityLbs,
  estimateDaysLeft,
  buildDrawdownBars,
  estimateSeasonFeedNeedLbs,
  resolveStationFillPercent,
} from './feedManagementUtils';
import { hopperOverfillWarning } from '../../map/utils/hopperCapacity';
import './feed-management-shell.css';

const FEED_TYPES = [
  'Corn',
  'Protein Pellets',
  'Rice Bran',
  'Corn/Soybean Mix',
  'Corn/Protein Mix',
  'Deer Draw',
  'Triple Grip',
  'Rack Attack',
  'Other Premium Blend',
].map((label) => ({ label, value: label.toLowerCase() }));

const DATE_RANGES = [
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: 'season', label: 'Season (~120 days)', days: 120 },
  { id: '6m', label: '6 months', days: 183 },
  { id: '12m', label: '12 months', days: 365 },
];

const FEED_TYPES_WITH_ALL = [{ label: 'All feed', value: 'all' }, ...FEED_TYPES];
const DAY_MS = 24 * 60 * 60 * 1000;
const STORE_HISTORY_KEY = 'y-feed-store-history';
const LOG_PAGE_SIZE = 5;
const SEASON_DAYS = 90;

function formatDateLabel(value) {
  try {
    return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (err) {
    return value;
  }
}

function formatRefillDue(value) {
  const date = localDateFrom(value);
  if (!date) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRefillShort(value) {
  const date = localDateFrom(value);
  if (!date) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toUpperCase();
}

function FeedManagementPage() {
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
  const [standSearch, setStandSearch] = useState('');
  const [standSort, setStandSort] = useState('name');
  const [selectedStationKey, setSelectedStationKey] = useState('');
  const [storesOpen, setStoresOpen] = useState(false);
  const [feedLogOpen, setFeedLogOpen] = useState(false);
  const [editingConsumptionKey, setEditingConsumptionKey] = useState(null);
  const [updatingActiveKey, setUpdatingActiveKey] = useState(null);
  const [logFeedOpen, setLogFeedOpen] = useState(false);

  useEffect(() => {
    if (!editingConsumptionKey) return undefined;
    const handlePointerDown = (event) => {
      if (!event.target.closest('[data-feed-consumption-root]')) {
        setEditingConsumptionKey(null);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [editingConsumptionKey]);

  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState('');
  const [feedEvents, setFeedEvents] = useState([]);
  const [feedEventsLoading, setFeedEventsLoading] = useState(false);
  const [feedEventsError, setFeedEventsError] = useState('');
  const [feedEventsRefreshKey, setFeedEventsRefreshKey] = useState(0);
  const [huntStats, setHuntStats] = useState(null);
  const [statsError, setStatsError] = useState('');
  const [rangeKey, setRangeKey] = useState('30d');
  const [feedTypeFilter, setFeedTypeFilter] = useState('all');
  const [manualEntry, setManualEntry] = useState({
    targetKey: '',
    amount: '',
    feedType: FEED_TYPES[0].value,
    note: '',
  });
  const [queueState, setQueueState] = useState(() => readQueue());
  const [clubs, setClubs] = useState([]);

  const loadOverview = useCallback(async () => {
    setOverviewError('');
    try {
      const data = await feedingService.getFeedingOverview();
      setOverview(data);
    } catch (err) {
      setOverviewError('Unable to load feeders right now.');
    }
  }, []);

  const loadHuntStats = useCallback(async () => {
    setStatsError('');
    try {
      const data = await huntService.getHuntStats();
      setHuntStats(data);
    } catch (err) {
      setStatsError('Unable to load deer sightings right now.');
    }
  }, []);

  useEffect(() => {
    loadOverview();
    loadHuntStats();
  }, [loadOverview, loadHuntStats]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/clubs/');
        if (!cancelled) {
          setClubs(Array.isArray(data) ? data : data?.results || []);
        }
      } catch (_) {
        if (!cancelled) setClubs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clubNameById = useMemo(() => buildClubNameMap(clubs), [clubs]);

  const selectedRange = DATE_RANGES.find((opt) => opt.id === rangeKey) || DATE_RANGES[1];
  const nextRefills = useMemo(
    () => personalFeedStations(Array.isArray(overview?.next_refills) ? overview.next_refills : []),
    [overview]
  );

  const feedTargets = useMemo(() => {
    const rateMap = feedingService?.RATE_LBS_PER_DAY || {};
    const mapped = nextRefills.map((item) => {
      const consumptionRateCode = item.consumption_rate_code || item.consumptionRateCode || '';
      const numericRate = Number(item.consumption_rate_lbs_per_day ?? item.consumptionRateLbsPerDay);
      const consumptionRateLbsPerDay =
        Number.isFinite(numericRate) && numericRate > 0
          ? numericRate
          : getConsumptionRateLbsPerDay(consumptionRateCode, rateMap);
      const percentRemaining =
        item.percent_remaining != null ? Number(item.percent_remaining) : null;
      const estimatedRemaining = Number(item.estimated_remaining_lbs) || 0;
      const storedCapacity =
        item.feed_capacity_lbs != null ? Number(item.feed_capacity_lbs) : null;
      const hopperCapacityLbs =
        Number.isFinite(storedCapacity) && storedCapacity > 0 ? storedCapacity : null;
      const capacityLbs =
        hopperCapacityLbs != null
          ? hopperCapacityLbs
          : estimateCapacityLbs(estimatedRemaining, percentRemaining);
      const daysLeft = estimateDaysLeft(
        estimatedRemaining,
        consumptionRateLbsPerDay,
        item.refill_due_date
      );
      const club = isClubFeeder(item);
      const targetKind = club ? 'club' : 'personal';
      return {
        key: `${targetKind}-${item.id}`,
        target: { kind: targetKind, id: item.id },
        name: item.name,
        scopeLabel: resolveFeederScopeLabel(item, clubNameById),
        isClub: club,
        clubId: item.club_id ?? null,
        feedType: (item.default_feed_type || item.feed_type || FEED_TYPES[0].value).toLowerCase(),
        estimatedRemaining,
        percentRemaining,
        capacityLbs: Number.isFinite(capacityLbs) ? capacityLbs : null,
        hopperCapacityLbs,
        daysLeft,
        refillDueDate: item.refill_due_date,
        feedingMode: (item.feeding_mode || '').toLowerCase(),
        consumptionRateCode: (consumptionRateCode || '').toLowerCase(),
        consumptionRateLbsPerDay,
        isActive: item.is_active !== false,
        status: getFeedLevelStatus(percentRemaining),
      };
    });
    return sortFeedTargets(mapped, 'name');
  }, [nextRefills, clubNameById]);

  const selectedLogTarget = useMemo(
    () => feedTargets.find((entry) => entry.key === manualEntry.targetKey) || null,
    [feedTargets, manualEntry.targetKey]
  );
  const logOverfillWarning = hopperOverfillWarning({
    amountLbs: Number(manualEntry.amount),
    capacityLbs: selectedLogTarget?.hopperCapacityLbs,
    remainingLbs: selectedLogTarget?.estimatedRemaining,
  });

  const filteredFeedTargets = useMemo(() => {
    if (!feedTargets.length) return [];
    const normalized = (feedTypeFilter || '').trim().toLowerCase();
    const byType =
      !normalized || normalized === 'all'
        ? feedTargets
        : feedTargets.filter((target) => (target.feedType || '') === normalized);
    const query = standSearch.trim().toLowerCase();
    const matched = !query
      ? byType
      : byType.filter(
          (target) =>
            (target.name || '').toLowerCase().includes(query) ||
            (target.scopeLabel || '').toLowerCase().includes(query)
        );
    return sortFeedTargets(matched, standSort);
  }, [feedTargets, feedTypeFilter, standSearch, standSort]);

  const activeFeedTargets = useMemo(
    () => feedTargets.filter((target) => target.isActive),
    [feedTargets]
  );

  const filteredActiveFeedTargets = useMemo(
    () => filteredFeedTargets.filter((target) => target.isActive),
    [filteredFeedTargets]
  );

  const activeRefills = useMemo(
    () => nextRefills.filter((item) => isFeederActive(item)),
    [nextRefills]
  );

  useEffect(() => {
    if (!feedTargets.length) {
      if (selectedStationKey) setSelectedStationKey('');
      return;
    }
    const stillValid = feedTargets.some((t) => t.key === selectedStationKey);
    if (stillValid) return;
    const firstActive = feedTargets.find((t) => t.isActive);
    const next = (firstActive || feedTargets[0]).key;
    setSelectedStationKey(next);
  }, [feedTargets, selectedStationKey]);

  const selectedStation = useMemo(() => {
    if (!feedTargets.length) return null;
    const matched = feedTargets.find((t) => t.key === selectedStationKey);
    if (matched) return matched;
    return feedTargets.find((t) => t.isActive) || feedTargets[0] || null;
  }, [feedTargets, selectedStationKey]);

  const effectiveSelectedKey = selectedStation?.key || '';

  const drawdownBars = useMemo(() => {
    if (!selectedStation) return [];
    return buildDrawdownBars({
      remainingLbs: selectedStation.estimatedRemaining,
      rateLbsPerDay: selectedStation.isActive ? selectedStation.consumptionRateLbsPerDay : 0,
      capacityLbs: selectedStation.capacityLbs,
      dayCount: 14,
    });
  }, [selectedStation]);

  const seasonFeedNeedLbs = useMemo(
    () => estimateSeasonFeedNeedLbs(activeFeedTargets, SEASON_DAYS),
    [activeFeedTargets]
  );

  const filteredFeedEvents = useMemo(() => {
    return filterFeedEventsByType(feedEvents, feedTypeFilter);
  }, [feedEvents, feedTypeFilter]);

  const feedTargetByKey = useMemo(() => {
    const map = new Map();
    feedTargets.forEach((target) => {
      map.set(target.key, target);
    });
    return map;
  }, [feedTargets]);

  const feedLogs = useMemo(() => {
    if (!Array.isArray(feedEvents) || feedEvents.length === 0) return [];
    return [...feedEvents]
      .sort((a, b) => {
        const aDate = new Date(a?.recorded_at || a?.date || a?.created_at || 0).getTime();
        const bDate = new Date(b?.recorded_at || b?.date || b?.created_at || 0).getTime();
        return bDate - aDate;
      })
      .map((event) => {
        const target = feedTargetByKey.get(event.targetKey);
        const feedTypeRaw = resolveFeedEventFeedType(event, target?.feedType);
        const amount = Number(event.amount_lbs);
        return {
          id: event.id || `${event.targetKey}-${event.recorded_at || event.date || Math.random()}`,
          dateLabel: formatDateLabel(event?.recorded_at || event?.date || event?.created_at),
          locationLabel: target?.name || event?.target_name || event?.location || '—',
          scopeLabel: target?.scopeLabel || '',
          amountLabel: Number.isFinite(amount) ? `${amount} lbs` : '—',
          feedTypeLabel: formatFeedTypeLabel(feedTypeRaw) || '—',
        };
      });
  }, [feedEvents, feedTargetByKey]);

  const feedLogPaginationKey = useMemo(
    () => [feedTypeFilter, feedLogs.length].join('|'),
    [feedTypeFilter, feedLogs.length]
  );

  const {
    items: feedLogPage,
    offset: feedLogOffset,
    limit: feedLogLimit,
    onPrev: feedLogOnPrev,
    onNext: feedLogOnNext,
  } = useClientPagination(feedLogs, LOG_PAGE_SIZE, feedLogPaginationKey);

  useEffect(() => {
    let cancelled = false;
    if (feedTargets.length === 0) {
      setFeedEvents([]);
      setFeedEventsError('');
      setFeedEventsLoading(false);
      return undefined;
    }
    const loadEvents = async () => {
      setFeedEventsLoading(true);
      setFeedEventsError('');
      const eventLimit = Math.max(50, Math.min(365, selectedRange.days * 2));
      try {
        const results = await Promise.all(
          feedTargets.map(async (target) => {
            try {
              const events = await feedingService.listFeedEvents(target.target, eventLimit);
              return (events || []).map((event) => ({
                ...event,
                targetKey: target.key,
                feed_type: resolveFeedEventFeedType(event, target.feedType),
              }));
            } catch (err) {
              return [];
            }
          })
        );
        if (!cancelled) setFeedEvents(results.flat());
      } catch (err) {
        if (!cancelled) {
          setFeedEvents([]);
          setFeedEventsError('Unable to load historical feed events.');
        }
      } finally {
        if (!cancelled) setFeedEventsLoading(false);
      }
    };
    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [feedTargets, selectedRange.days, feedEventsRefreshKey]);

  useEffect(() => {
    if (!manualEntry.targetKey && feedTargets.length > 0) {
      setManualEntry((prev) => ({
        ...prev,
        targetKey: feedTargets[0].key,
        feedType: feedTargets[0].feedType || prev.feedType,
      }));
    }
  }, [feedTargets, manualEntry.targetKey]);

  const totalRemainingLbs = useMemo(() => {
    return activeFeedTargets.reduce((sum, target) => sum + target.estimatedRemaining, 0);
  }, [activeFeedTargets]);

  const urgentTasks = useMemo(() => buildUrgentRefillTasks(activeRefills), [activeRefills]);
  const attentionCount = useMemo(() => countFeedersNeedingAttention(activeRefills), [activeRefills]);

  const deerSightings = useMemo(() => {
    const chartData = Array.isArray(huntStats?.chart_data) ? huntStats.chart_data : [];
    return chartData.map((entry) => ({
      date: entry.date,
      total_deer_seen: entry.deer_seen,
    }));
  }, [huntStats]);

  const { rangeStart, rangeEnd } = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - (selectedRange.days - 1) * DAY_MS);
    return { rangeStart: start, rangeEnd: end };
  }, [selectedRange]);

  const feedInventorySeries = useMemo(() => {
    return buildFeedInventorySeries({
      feedEvents: filteredFeedEvents,
      deerSightings,
      feedTargets: filteredActiveFeedTargets,
      rangeStart,
      rangeEnd,
    });
  }, [filteredFeedEvents, deerSightings, filteredActiveFeedTargets, rangeStart, rangeEnd]);

  const {
    selection: feedBrushSelection,
    onChange: handleFeedBrushChange,
    reset: resetFeedBrush,
    summary: feedBrushSummary,
    version: feedBrushVersion,
  } = useBrushSelection({
    data: feedInventorySeries,
    dataKey: 'date',
  });

  useEffect(() => {
    resetFeedBrush();
  }, [resetFeedBrush, rangeKey, feedTypeFilter, feedInventorySeries]);

  const formatTooltipValue = useCallback((value, name, payloadInfo) => {
    if (payloadInfo?.dataKey === 'feedInventoryLbs') {
      const pounds = payloadInfo?.payload?.feedInventoryLbs ?? 0;
      const normalized = Number.isFinite(pounds) ? pounds : 0;
      const display = Number.isInteger(normalized) ? normalized : normalized.toFixed(1);
      return [`${display} lbs`];
    }
    if (payloadInfo?.dataKey === 'deerCount') {
      if (value == null) return ['--'];
      return [`${Number(value)} deer`];
    }
    return [value, name];
  }, []);

  const consumptionSummary = useMemo(() => {
    return buildConsumptionSummary({
      feedTargets: filteredActiveFeedTargets,
      events: filteredFeedEvents,
      deerSightings,
      estimatedRemainingLbs: totalRemainingLbs,
      projectionDays: [7, 30, 90],
      windowDays: selectedRange.days,
    });
  }, [
    filteredActiveFeedTargets,
    filteredFeedEvents,
    deerSightings,
    totalRemainingLbs,
    selectedRange.days,
  ]);

  const handleManualFieldChange = (field, value) => {
    setManualEntry((prev) => ({ ...prev, [field]: value }));
  };

  const handleTargetChange = (value) => {
    const target = feedTargets.find((entry) => entry.key === value);
    setManualEntry((prev) => ({
      ...prev,
      targetKey: value,
      feedType: target?.feedType || prev.feedType,
    }));
  };

  const flushQueueWithService = useCallback(async () => {
    const result = await flushQueue({
      sendFn: (event) => feedingService.addFeedEvent(event.target, event.payload),
    });
    if (result.flushed > 0) {
      await loadOverview();
      setFeedEventsRefreshKey((key) => key + 1);
      try {
        showToast({
          title: 'Synced feed logs',
          body: `Uploaded ${result.flushed} pending entries.`,
          variant: 'success',
        });
      } catch (_) {}
    }
    setQueueState(readQueue());
    return result;
  }, [loadOverview, showToast]);

  useEffect(() => {
    const handleOnline = () => {
      flushQueueWithService().catch(() => {});
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [flushQueueWithService]);

  const handleManualSubmit = async (event) => {
    event.preventDefault();
    const target = feedTargets.find((entry) => entry.key === manualEntry.targetKey);
    if (!target) {
      showToast({ title: 'Select a feeder', variant: 'warning' });
      return;
    }
    const amount = Number(manualEntry.amount);
    if (!Number.isFinite(amount) || amount === 0) {
      showToast({ title: 'Enter amount', body: 'Amount must be non-zero.', variant: 'warning' });
      return;
    }
    const overfill = hopperOverfillWarning({
      amountLbs: amount,
      capacityLbs: target.hopperCapacityLbs,
      remainingLbs: target.estimatedRemaining,
    });
    if (overfill) {
      return;
    }
    const payload = {
      amount_lbs: amount,
      feed_type: manualEntry.feedType,
      note: manualEntry.note ? manualEntry.note.trim() : undefined,
    };
    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    if (!isOnline) {
      enqueueOfflineEvent({ target: target.target, payload });
      setQueueState(readQueue());
      setManualEntry((prev) => ({ ...prev, amount: '', note: '' }));
      setLogFeedOpen(false);
      showToast({
        title: 'Queued offline',
        body: 'Stored locally until you reconnect.',
        variant: 'info',
      });
      return;
    }
    try {
      await feedingService.addFeedEvent(target.target, payload);
      setManualEntry((prev) => ({ ...prev, amount: '', note: '' }));
      setLogFeedOpen(false);
      await loadOverview();
      setFeedEventsRefreshKey((key) => key + 1);
      showToast({ title: 'Feed logged', variant: 'success' });
    } catch (err) {
      showToast({ title: 'Failed to log feed', variant: 'danger' });
    }
  };

  const handleToggleActive = async (targetKey) => {
    const target = feedTargets.find((entry) => entry.key === targetKey);
    if (!target) return;
    const nextActive = !target.isActive;
    setUpdatingActiveKey(targetKey);
    try {
      await feedingService.updateFeederActive(target.target, nextActive);
      showToast({
        title: nextActive ? 'Feeder reactivated' : 'Feeder deactivated',
        body: nextActive
          ? `${target.name} is active again and included in metrics.`
          : `${target.name} is hidden from metrics until reactivated.`,
        variant: 'success',
      });
      loadOverview();
    } catch (err) {
      showToast({ title: 'Unable to update feeder status', variant: 'danger' });
    } finally {
      setUpdatingActiveKey(null);
    }
  };

  const handleConsumptionRateChange = async (targetKey, nextCode) => {
    const normalized = (nextCode || '').trim().toLowerCase();
    if (!normalized) return;
    const target = feedTargets.find((entry) => entry.key === targetKey);
    if (!target || target.consumptionRateCode === normalized) {
      setEditingConsumptionKey(null);
      return;
    }
    setEditingConsumptionKey(null);
    try {
      await feedingService.updateConsumptionRateCode(target.target, normalized);
      showToast({
        title: 'Consumption rate updated',
        body: `${target.name}: ${getConsumptionRateOptionLabel(normalized) || normalized}.`,
        variant: 'success',
      });
      loadOverview();
    } catch (err) {
      showToast({ title: 'Unable to update consumption rate', variant: 'danger' });
    }
  };

  const handleDefaultFeedType = async (targetKey, nextFeedType) => {
    const target = feedTargets.find((entry) => entry.key === targetKey);
    if (!target) return;
    try {
      await feedingService.updateDefaultFeedType(target.target, nextFeedType);
      showToast({
        title: 'Default updated',
        body: `${target.name} now defaults to ${formatFeedTypeLabel(nextFeedType)}.`,
        variant: 'success',
      });
      loadOverview();
    } catch (err) {
      showToast({ title: 'Unable to update default', variant: 'danger' });
    }
  };

  const openLogFeedForTarget = (targetKey) => {
    if (targetKey) handleTargetChange(targetKey);
    setLogFeedOpen(true);
  };

  const renderFillClass = (percent) => {
    if (percent == null) return 'feed-mgmt-fill feed-mgmt-fill--warn';
    if (percent > 50) return 'feed-mgmt-fill feed-mgmt-fill--good';
    if (percent > 20) return 'feed-mgmt-fill feed-mgmt-fill--warn';
    return 'feed-mgmt-fill feed-mgmt-fill--danger';
  };

  const renderOutlookChartBody = () => {
    if (statsError) {
      return (
        <p className="feed-mgmt-empty feed-mgmt-empty--error mb-0" role="alert">
          {statsError}
        </p>
      );
    }
    if (feedEventsError) {
      return (
        <p className="feed-mgmt-empty feed-mgmt-empty--error mb-0" role="alert">
          {feedEventsError}
        </p>
      );
    }
    if (filteredActiveFeedTargets.length === 0) {
      return (
        <p className="feed-mgmt-empty mb-0">
          {activeFeedTargets.length === 0
            ? 'Configure active feeders to see inventory trends.'
            : 'No active feeders match this feed type.'}
        </p>
      );
    }
    if (feedEventsLoading && feedInventorySeries.length === 0) {
      return <p className="feed-mgmt-empty mb-0">Loading feed history…</p>;
    }
    if (feedInventorySeries.length === 0) {
      return <p className="feed-mgmt-empty mb-0">Not enough data to build this range yet.</p>;
    }
    return (
      <div className="feed-mgmt-outlook-chart-well">
        <YFeedOutlookChart
          data={feedInventorySeries}
          height={240}
          xTickFormatter={formatDateLabel}
          tooltipLabelFormatter={formatDateLabel}
          tooltipValueFormatter={formatTooltipValue}
          brush={{
            enabled: true,
            selection: feedBrushSelection,
            onChange: handleFeedBrushChange,
            version: feedBrushVersion,
          }}
          brushSummary={feedBrushSummary}
          brushActions={
            feedBrushSummary ? (
              <YButton
                variant="ghost"
                size="sm"
                aria-label="Reset zoom"
                title="Reset zoom"
                onClick={resetFeedBrush}
              >
                <FaSync />
              </YButton>
            ) : null
          }
        />
      </div>
    );
  };

  const selectedInactive = selectedStation && !selectedStation.isActive;
  const selectedDaysLabel =
    selectedStation?.daysLeft != null ? `${selectedStation.daysLeft} DAYS` : '—';

  return (
    <div className="feed-mgmt-shell feed-mgmt-shell--field" data-testid="feed-management-page">
      <ToolkitShellTabs />

      <div className="feed-mgmt-expand-strips" data-testid="feed-expand-strips">
        <div className="feed-mgmt-stores-strip" data-testid="feed-stores-strip">
          <button
            type="button"
            className={`feed-mgmt-stores-strip__toggle${storesOpen ? ' is-open' : ''}`}
            aria-expanded={storesOpen}
            aria-controls="feed-stores-panel"
            onClick={() => setStoresOpen((open) => !open)}
            data-testid="feed-stores-toggle"
          >
            <FaStore aria-hidden="true" />
            <span>FIND FEED STORES</span>
            <FaChevronDown
              className={`feed-mgmt-stores-strip__chevron${storesOpen ? ' is-open' : ''}`}
              aria-hidden="true"
            />
          </button>
          {storesOpen ? (
            <div
              id="feed-stores-panel"
              className="feed-mgmt-stores-strip__panel"
              data-testid="feed-tab-stores"
            >
              <div className="feed-mgmt-stores-layout">
                <div className="feed-mgmt-stores-main">
                  <section className="feed-mgmt-stores-panel">
                    <div className="feed-mgmt-stores-panel__header">
                      <FaStore aria-hidden="true" />
                      <span>My stores</span>
                    </div>
                    <div className="feed-mgmt-stores-panel__body">
                      <MyStoresCard appearance="feed-mgmt" />
                    </div>
                  </section>
                  <section className="feed-mgmt-stores-panel feed-mgmt-stores-panel--finder">
                    <div className="feed-mgmt-stores-panel__header">
                      <FaStore aria-hidden="true" />
                      <span>Find feed &amp; seed stores</span>
                    </div>
                    <div className="feed-mgmt-stores-panel__body">
                      <SeedFeedFinder
                        appearance="feed-mgmt"
                        historyStorageKey={STORE_HISTORY_KEY}
                      />
                    </div>
                  </section>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="feed-mgmt-stores-strip" data-testid="feed-log-strip">
          <button
            type="button"
            className={`feed-mgmt-stores-strip__toggle${feedLogOpen ? ' is-open' : ''}`}
            aria-expanded={feedLogOpen}
            aria-controls="feed-log-panel"
            onClick={() => setFeedLogOpen((open) => !open)}
            data-testid="feed-log-toggle"
          >
            <FaHistory aria-hidden="true" />
            <span>FEED LOG</span>
            <FaChevronDown
              className={`feed-mgmt-stores-strip__chevron${feedLogOpen ? ' is-open' : ''}`}
              aria-hidden="true"
            />
          </button>
          {feedLogOpen ? (
            <div
              id="feed-log-panel"
              className="feed-mgmt-stores-strip__panel"
              data-testid="feed-mgmt-feed-log"
            >
              {feedLogPage.length === 0 ? (
                <p className="feed-mgmt-empty mb-0">No feed events yet.</p>
              ) : (
                <>
                  <ul className="feed-mgmt-feed-log__list">
                    {feedLogPage.map((row) => (
                      <li key={row.id} className="feed-mgmt-feed-log__row">
                        <div className="feed-mgmt-feed-log__main">
                          <span className="feed-mgmt-feed-log__row-icon" aria-hidden="true">
                            <GiCorn size={16} />
                          </span>
                          <div className="feed-mgmt-feed-log__copy">
                            <p className="feed-mgmt-feed-log__stand">{row.locationLabel}</p>
                            <p className="feed-mgmt-feed-log__meta">
                              {row.dateLabel}
                              {row.scopeLabel ? ` · ${row.scopeLabel}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="feed-mgmt-feed-log__aside">
                          <span className="feed-mgmt-feed-log__amount">{row.amountLabel}</span>
                          <span className="feed-mgmt-feed-log__type">{row.feedTypeLabel}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <YPagination
                    offset={feedLogOffset}
                    limit={feedLogLimit}
                    count={feedLogs.length}
                    onPrev={feedLogOnPrev}
                    onNext={feedLogOnNext}
                  />
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="feed-mgmt-log-bar">
        <YButton
          variant="primary"
          className="feed-mgmt-log-btn feed-mgmt-log-btn--stretch"
          data-testid="feed-mgmt-log-feed-btn"
          onClick={() => setLogFeedOpen(true)}
          disabled={feedTargets.length === 0}
        >
          <FaPlus aria-hidden="true" />
          <span className="feed-mgmt-log-btn__label">LOG FEED</span>
        </YButton>
      </div>

      <div className="feed-mgmt-inner">
        <div className="feed-mgmt-stats feed-mgmt-stats--field">
          <div className="feed-mgmt-stat">
            <div className="feed-mgmt-stat__icon feed-mgmt-stat__icon--good">
              <FaBullseye aria-hidden="true" />
            </div>
            <div>
              <p className="feed-mgmt-stat__value">{activeFeedTargets.length}</p>
              <p className="feed-mgmt-stat__label">Active feeders</p>
            </div>
          </div>
          <div className="feed-mgmt-stat">
            <div className="feed-mgmt-stat__icon feed-mgmt-stat__icon--warn">
              <FaExclamationTriangle aria-hidden="true" />
            </div>
            <div>
              <p className="feed-mgmt-stat__value">{attentionCount}</p>
              <p className="feed-mgmt-stat__label">Need attention</p>
            </div>
          </div>
          <div className="feed-mgmt-stat">
            <div className="feed-mgmt-stat__icon feed-mgmt-stat__icon--info">
              <FaBox aria-hidden="true" />
            </div>
            <div>
              <p className="feed-mgmt-stat__value">{Math.round(totalRemainingLbs)}</p>
              <p className="feed-mgmt-stat__label">Lbs at feeders</p>
            </div>
          </div>
          <div className="feed-mgmt-stat" data-testid="feed-stat-daily-consumption">
            <div className="feed-mgmt-stat__icon feed-mgmt-stat__icon--info">
              <FaHistory aria-hidden="true" />
            </div>
            <div>
              <p className="feed-mgmt-stat__value">
                {consumptionSummary.avgDailyUse
                  ? consumptionSummary.avgDailyUse.toFixed(1)
                  : '—'}
              </p>
              <p className="feed-mgmt-stat__label">Daily consumption</p>
            </div>
          </div>
          <div className="feed-mgmt-stat" data-testid="feed-stat-days-until-empty">
            <div className="feed-mgmt-stat__icon feed-mgmt-stat__icon--warn">
              <FaExclamationTriangle aria-hidden="true" />
            </div>
            <div>
              <p className="feed-mgmt-stat__value">
                {consumptionSummary.daysUntilEmpty ?? '—'}
              </p>
              <p className="feed-mgmt-stat__label">Days until empty</p>
            </div>
          </div>
        </div>

        {urgentTasks.length > 0 ? (
          <div className="feed-mgmt-alert-panel" data-testid="feed-mgmt-attention">
            <h2 className="feed-mgmt-alert-panel__title">
              <FaExclamationTriangle aria-hidden="true" />
              Attention required
            </h2>
            {urgentTasks.map((task) => (
              <div
                key={task.id}
                className={`feed-mgmt-alert-row feed-mgmt-alert-row--${task.priority}`}
              >
                <div>
                  <div className="fw-semibold">{task.stand}</div>
                  <div className="small text-muted">{task.task}</div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="font-monospace small">{formatRefillDue(task.dueDate)}</span>
                  <YButton
                    variant="danger"
                    size="sm"
                    className="feed-mgmt-alert-log-btn"
                    onClick={() => openLogFeedForTarget(`${task.target.kind}-${task.target.id}`)}
                  >
                    Log feed
                  </YButton>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="feed-mgmt-field-grid" data-testid="feed-tab-stands">
          <section className="feed-mgmt-enamel-panel" aria-labelledby="feed-stations-title">
            <header className="feed-mgmt-enamel-panel__head">
              <h2 id="feed-stations-title">FEED STATIONS</h2>
            </header>

            <div className="feed-mgmt-stand-toolbar feed-mgmt-stand-toolbar--compact">
              <YLogSearchInput
                id="feed-stand-search"
                value={standSearch}
                onChange={(e) => setStandSearch(e.target.value)}
                placeholder="Search stands…"
                ariaLabel="Search stands"
                className="feed-mgmt-stand-toolbar__search"
              />
              <div className="feed-mgmt-stand-toolbar__sort">
                <label className="feed-mgmt-stand-toolbar__sort-label" htmlFor="feed-stand-sort">
                  Sort
                </label>
                <select
                  id="feed-stand-sort"
                  className="y-select feed-mgmt-stand-toolbar__sort-select"
                  value={standSort}
                  onChange={(e) => setStandSort(e.target.value)}
                  aria-label="Sort stands by"
                  data-testid="feed-stand-sort"
                >
                  {FEED_STAND_SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {overviewError ? <div className="text-danger small mb-3 px-3">{overviewError}</div> : null}

            {filteredFeedTargets.length === 0 ? (
              <p className="feed-mgmt-empty">
                {feedTargets.length === 0
                  ? 'Configure feeders on your map to track them here.'
                  : 'No stands match your search or feed filter.'}
              </p>
            ) : (
              <ul className="feed-mgmt-station-list" role="listbox" aria-label="Feed stations">
                {filteredFeedTargets.map((stand) => {
                  const isInactive = !stand.isActive;
                  const isSelected = effectiveSelectedKey === stand.key;
                  const isUpdatingActive = updatingActiveKey === stand.key;
                  const widthPct = resolveStationFillPercent({
                    estimatedRemaining: stand.estimatedRemaining,
                    percentRemaining: stand.percentRemaining,
                    capacityLbs: stand.capacityLbs,
                  });
                  const capacityLabel =
                    stand.capacityLbs != null
                      ? `${Math.round(stand.estimatedRemaining)} / ${Math.round(stand.capacityLbs)} LB`
                      : `${Math.round(stand.estimatedRemaining)} LB`;
                  const daysLabel =
                    stand.daysLeft != null ? `${stand.daysLeft} DAYS` : '—';
                  return (
                    <li key={stand.key} role="none">
                      <div
                        className={`feed-mgmt-station-row${isSelected ? ' is-selected' : ''}${
                          isInactive ? ' is-inactive' : ''
                        }`}
                        data-testid={`feed-station-row-${stand.key}`}
                      >
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className="feed-mgmt-station-row__main"
                          onClick={() => setSelectedStationKey(stand.key)}
                        >
                          <span className="feed-mgmt-station-row__topline">
                            <h3 className="feed-mgmt-station-row__name">{stand.name}</h3>
                            <span
                              className={`feed-mgmt-station-row__days${
                                stand.status === 'low' || stand.status === 'empty'
                                  ? ' is-urgent'
                                  : ''
                              }`}
                            >
                              {isInactive ? 'OFF' : daysLabel}
                            </span>
                          </span>
                          <span className="feed-mgmt-station-row__meta">
                            {stand.scopeLabel}
                            {getFeedingModeLabel(stand.feedingMode)
                              ? ` · ${getFeedingModeLabel(stand.feedingMode)}`
                              : ''}
                            {isInactive ? ' · Deactivated' : ''}
                          </span>
                          <span
                            className="feed-mgmt-fill-track feed-mgmt-fill-track--station"
                            data-testid={`feed-station-fill-${stand.key}`}
                          >
                            <span
                              className={renderFillClass(widthPct)}
                              style={{ width: `${widthPct}%` }}
                              role="progressbar"
                              aria-valuenow={Math.round(widthPct)}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-label={`${formatFeedTypeLabel(stand.feedType)} level ${Math.round(widthPct)}%`}
                            />
                          </span>
                          <span className="feed-mgmt-station-row__footer">
                            <span>{capacityLabel}</span>
                            <span>REFILL {formatRefillShort(stand.refillDueDate)}</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!isInactive}
                          aria-label={
                            isInactive
                              ? `Reactivate ${stand.name}`
                              : `Deactivate ${stand.name}`
                          }
                          className={`feed-mgmt-station-toggle${isInactive ? '' : ' is-on'}`}
                          disabled={isUpdatingActive}
                          data-testid={`feed-toggle-active-${stand.key}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleToggleActive(stand.key);
                          }}
                        >
                          <span className="feed-mgmt-station-toggle__knob" aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="feed-mgmt-enamel-panel__actions">
              <YButton
                variant="primary"
                size="sm"
                className="feed-mgmt-enamel-btn feed-mgmt-enamel-btn--accent"
                data-testid="feed-add-station-btn"
                onClick={() => navigate('/maps')}
              >
                <FaPlus aria-hidden="true" /> ADD STATION
              </YButton>
              <YButton
                variant="secondary"
                size="sm"
                className="feed-mgmt-enamel-btn"
                data-testid="feed-log-a-fill-btn"
                onClick={() => openLogFeedForTarget(effectiveSelectedKey || selectedStationKey)}
                disabled={feedTargets.length === 0}
              >
                LOG A FILL
              </YButton>
            </div>
          </section>

          <section
            className={`feed-mgmt-enamel-panel feed-mgmt-drawdown${
              selectedInactive ? ' is-muted' : ''
            }`}
            aria-labelledby="feed-drawdown-title"
            data-testid="feed-drawdown-panel"
          >
            {selectedStation ? (
              <>
                <header className="feed-mgmt-drawdown__head">
                  <h2 id="feed-drawdown-title" className="feed-mgmt-drawdown__title">
                    DRAW-DOWN · {selectedStation.name}
                  </h2>
                  <span className="feed-mgmt-drawdown__days">{selectedDaysLabel}</span>
                </header>

                <div className="feed-mgmt-drawdown__hero">
                  <div className="feed-mgmt-drawdown__remain">
                    {Math.round(selectedStation.estimatedRemaining)}
                  </div>
                  <div className="feed-mgmt-drawdown__remain-meta">
                    LB REMAINING
                    <br />
                    EMPTY {formatRefillShort(selectedStation.refillDueDate)}
                  </div>
                </div>

                <div
                  className="feed-mgmt-drawdown__bars"
                  aria-hidden="true"
                  data-testid="feed-drawdown-bars"
                >
                  {drawdownBars.map((bar) => (
                    <div
                      key={bar.day}
                      className={`feed-mgmt-drawdown__bar feed-mgmt-drawdown__bar--${bar.urgency}`}
                      style={{ height: `${Math.max(4, bar.heightPct)}%` }}
                    />
                  ))}
                </div>
                <div className="feed-mgmt-drawdown__bar-labels">
                  <span>TODAY</span>
                  <span>+7D</span>
                  <span>+14D</span>
                </div>

                <div
                  className="feed-mgmt-drawdown__rate"
                  data-feed-consumption-root
                  data-testid={`feed-consumption-root-${selectedStation.key}`}
                >
                  <div className="feed-mgmt-drawdown__rate-label">
                    <span>DRAW-DOWN RATE</span>
                    <span>{selectedStation.consumptionRateLbsPerDay} LB/DAY</span>
                  </div>
                  <button
                    type="button"
                    className={`feed-mgmt-consumption__trigger feed-mgmt-drawdown__rate-trigger${
                      editingConsumptionKey === selectedStation.key ? ' is-open' : ''
                    }`}
                    aria-expanded={editingConsumptionKey === selectedStation.key}
                    aria-haspopup="listbox"
                    aria-controls={`feed-consumption-${selectedStation.key}`}
                    aria-label={`Consumption rate: ${
                      getConsumptionRateOptionLabel(selectedStation.consumptionRateCode) ||
                      `${selectedStation.consumptionRateLbsPerDay} lbs per day`
                    }. Click to change.`}
                    disabled={selectedInactive}
                    onClick={() =>
                      setEditingConsumptionKey((prev) =>
                        prev === selectedStation.key ? null : selectedStation.key
                      )
                    }
                    data-testid={`feed-consumption-trigger-${selectedStation.key}`}
                  >
                    <span>
                      {getConsumptionRateOptionLabel(selectedStation.consumptionRateCode) ||
                        `${selectedStation.consumptionRateLbsPerDay} lbs/day`}
                    </span>
                    <FaChevronDown aria-hidden="true" />
                  </button>
                  {editingConsumptionKey === selectedStation.key ? (
                    <ul
                      id={`feed-consumption-${selectedStation.key}`}
                      className="feed-mgmt-consumption__menu"
                      role="listbox"
                      aria-label={`Consumption rate for ${selectedStation.name}`}
                      data-testid={`feed-consumption-menu-${selectedStation.key}`}
                    >
                      {FEED_CONSUMPTION_RATE_OPTIONS.map((opt) => {
                        const isSelected = selectedStation.consumptionRateCode === opt.value;
                        return (
                          <li key={opt.value} role="none">
                            <button
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              className={`feed-mgmt-consumption__option${
                                isSelected ? ' is-selected' : ''
                              }`}
                              onClick={() =>
                                handleConsumptionRateChange(selectedStation.key, opt.value)
                              }
                              data-testid={`feed-consumption-option-${selectedStation.key}-${opt.value}`}
                            >
                              {opt.label}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>

                <div className="feed-mgmt-drawdown__default-feed">
                  <label
                    className="feed-mgmt-stand-card__default-label"
                    htmlFor={`feed-default-${selectedStation.key}`}
                  >
                    Default feed
                  </label>
                  <span
                    className="feed-mgmt-feed-dot"
                    style={{ background: getFeedTypeDotColor(selectedStation.feedType) }}
                    aria-hidden="true"
                  />
                  <select
                    id={`feed-default-${selectedStation.key}`}
                    className="y-select feed-mgmt-stand-card__default-select"
                    value={selectedStation.feedType}
                    disabled={selectedInactive}
                    onChange={(event) =>
                      handleDefaultFeedType(selectedStation.key, event.target.value)
                    }
                  >
                    {FEED_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedStation.capacityLbs != null ? (
                  <div className="feed-mgmt-drawdown__capacity">
                    <div className="feed-mgmt-drawdown__rate-label">
                      <span>CAPACITY</span>
                      <span>{Math.round(selectedStation.capacityLbs)} LB</span>
                    </div>
                    <p className="feed-mgmt-drawdown__hint">
                      Edit hopper capacity on the map when configuring the station.
                    </p>
                  </div>
                ) : null}

                <div className="feed-mgmt-drawdown__season">
                  <div>
                    <div className="feed-mgmt-drawdown__season-label">FEED TO SEASON&apos;S END</div>
                    <div className="feed-mgmt-drawdown__season-value">
                      {seasonFeedNeedLbs.toLocaleString()}{' '}
                      <span className="feed-mgmt-drawdown__season-unit">LB</span>
                    </div>
                  </div>
                  <div>
                    <div className="feed-mgmt-drawdown__season-label">ACTIVE STATIONS</div>
                    <div className="feed-mgmt-drawdown__season-value">
                      {activeFeedTargets.length}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="feed-mgmt-empty">Select a feed station to see draw-down.</p>
            )}
          </section>
        </div>

        <section
          className="feed-mgmt-section feed-mgmt-outlook"
          data-testid="feed-tab-outlook"
          aria-labelledby="feed-mgmt-outlook-chart-title"
        >
          <div className="feed-mgmt-outlook-panel">
            <header className="feed-mgmt-outlook-panel__head">
              <div className="feed-mgmt-outlook-panel__head-main">
                <FaChartBar className="feed-mgmt-outlook-panel__icon" aria-hidden="true" />
                <div>
                  <h2 id="feed-mgmt-outlook-chart-title" className="feed-mgmt-outlook-panel__title">
                    Feed vs deer activity
                  </h2>
                  <p className="feed-mgmt-outlook-panel__sub">
                    Cumulative across active stations · {selectedRange.label}
                  </p>
                </div>
              </div>
            </header>
            <div className="feed-mgmt-outlook-panel__body">
              <div className="feed-mgmt-outlook-toolbar">
                <div className="feed-mgmt-outlook-toolbar__group">
                  <span className="feed-mgmt-outlook-toolbar__label">Range</span>
                  <div className="feed-mgmt-range-toggle">
                    {DATE_RANGES.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`y-pill-button ${rangeKey === option.id ? 'active' : ''}`}
                        onClick={() => setRangeKey(option.id)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="feed-mgmt-outlook-toolbar__group">
                  <label className="feed-mgmt-outlook-toolbar__label" htmlFor="feed-type-filter-outlook">
                    Feed type
                  </label>
                  <select
                    id="feed-type-filter-outlook"
                    className="feed-mgmt-outlook-toolbar__select"
                    value={feedTypeFilter}
                    onChange={(e) => setFeedTypeFilter(e.target.value)}
                  >
                    {FEED_TYPES_WITH_ALL.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {renderOutlookChartBody()}
            </div>
          </div>
        </section>

        {urgentTasks.length > 0 ? (
          <section
            className="feed-mgmt-feed-log feed-mgmt-section-card"
            aria-labelledby="feed-mgmt-refill-schedule-title"
            data-testid="feed-mgmt-refill-schedule"
          >
            <header className="feed-mgmt-feed-log__head">
              <div className="feed-mgmt-feed-log__head-text">
                <FaExclamationTriangle className="feed-mgmt-feed-log__icon" aria-hidden="true" />
                <div>
                  <h2 id="feed-mgmt-refill-schedule-title" className="feed-mgmt-feed-log__title">
                    Recommended refill schedule
                  </h2>
                </div>
              </div>
            </header>
            <ul className="feed-mgmt-feed-log__list">
              {urgentTasks.map((task) => (
                <li key={task.id} className="feed-mgmt-feed-log__row">
                  <div className="feed-mgmt-feed-log__main">
                    <span className="feed-mgmt-feed-log__row-icon" aria-hidden="true">
                      <FaBullseye size={16} />
                    </span>
                    <div className="feed-mgmt-feed-log__copy">
                      <p className="feed-mgmt-feed-log__stand">{task.stand}</p>
                      <p className="feed-mgmt-feed-log__meta">{task.task}</p>
                    </div>
                  </div>
                  <div className="feed-mgmt-feed-log__aside">
                    <span className="feed-mgmt-status-pill feed-mgmt-status-pill--low">
                      {formatRefillDue(task.dueDate)}
                    </span>
                    <YButton
                      variant="secondary"
                      size="sm"
                      onClick={() => openLogFeedForTarget(`${task.target.kind}-${task.target.id}`)}
                    >
                      Log feed
                    </YButton>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <YSheet
        show={logFeedOpen}
        onHide={() => setLogFeedOpen(false)}
        kicker="LOG"
        title="Log feed"
        className="feed-log-dialog"
        testId="feed-log-dialog"
        footer={
          <>
            <YButton variant="secondary" onClick={() => setLogFeedOpen(false)}>
              Cancel
            </YButton>
            <YButton
              variant="primary"
              type="submit"
              form="feed-log-form"
              data-testid="feed-log-submit-btn"
              disabled={Boolean(logOverfillWarning)}
            >
              <FaPlus aria-hidden="true" /> Log feed
            </YButton>
          </>
        }
      >
        <form id="feed-log-form" className="feed-mgmt-log-dialog" onSubmit={handleManualSubmit}>
          <div className="feed-mgmt-log-dialog__row">
            <div>
              <label className="y-label--dialog" htmlFor="feed-target-select">
                Select stand
              </label>
              <YSelect
                id="feed-target-select"
                value={manualEntry.targetKey}
                onChange={(event) => handleTargetChange(event.target.value)}
              >
                {feedTargets.map((target) => (
                  <option key={target.key} value={target.key}>
                    {target.name} · {target.scopeLabel}
                  </option>
                ))}
              </YSelect>
            </div>
          </div>

          <div className="feed-mgmt-log-dialog__row">
            <div>
              <label className="y-label--dialog" htmlFor="feed-type-select">
                Feed type
              </label>
              <YSelect
                id="feed-type-select"
                value={manualEntry.feedType}
                onChange={(event) => handleManualFieldChange('feedType', event.target.value)}
              >
                {FEED_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </YSelect>
            </div>
          </div>

          <div className="feed-mgmt-log-dialog__row feed-mgmt-log-dialog__row--2">
            <div>
              <label className="y-label--dialog" htmlFor="feed-amount-input">
                Amount (lbs)
              </label>
              <YInput
                id="feed-amount-input"
                type="number"
                value={manualEntry.amount}
                onChange={(event) => handleManualFieldChange('amount', event.target.value)}
                placeholder="50"
              />
              {logOverfillWarning ? (
                <div className="small text-danger mt-1" data-testid="feed-log-overfill">
                  {logOverfillWarning}
                </div>
              ) : null}
            </div>
            <div>
              <label className="y-label--dialog" htmlFor="feed-cost-input">
                Cost (optional)
              </label>
              <YInput id="feed-cost-input" type="number" step="0.01" placeholder="0.00" disabled />
            </div>
          </div>

          <div className="feed-mgmt-log-dialog__row">
            <div>
              <label className="y-label--dialog" htmlFor="feed-note-input">
                Notes
              </label>
              <YInput
                id="feed-note-input"
                type="textarea"
                rows={2}
                value={manualEntry.note}
                onChange={(event) => handleManualFieldChange('note', event.target.value)}
                placeholder="Any observations…"
              />
            </div>
          </div>

          <p className="text-muted small mb-0">
            {queueState.length > 0
              ? `${queueState.length} pending offline`
              : browserOnline
                ? 'Online'
                : 'Offline'}
          </p>
        </form>
      </YSheet>
    </div>
  );
}

export default FeedManagementPage;
